import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/50">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <span className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-layout-grid-line text-background-50 text-sm"></i>
              </span>
              <span className="text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors hidden sm:block">
                OOPBUY Spreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              <Link
                to="/"
                className="whitespace-nowrap cursor-pointer px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              >
                Startseite
              </Link>
              <Link
                to={`/artikel/${articles[0].slug}`}
                className="whitespace-nowrap cursor-pointer px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors"
              >
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap cursor-pointer ml-3 inline-flex items-center gap-1.5 px-5 py-2 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors"
              >
                Buy it now
                <i className="ri-arrow-right-up-line text-xs"></i>
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              aria-label="Menü öffnen"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl text-foreground-700`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div
            ref={menuRef}
            className="absolute top-16 right-0 w-[85vw] max-w-sm h-[calc(100vh-4rem)] bg-background-50 border-l border-background-200 overflow-y-auto"
          >
            <div className="p-5">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-background-100 text-foreground-800 font-semibold text-sm mb-3 cursor-pointer hover:bg-primary-50 transition-colors"
              >
                <i className="ri-home-4-line text-lg text-primary-500"></i>
                Startseite
              </Link>
              <Link
                to={`/artikel/${articles[0].slug}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3.5 rounded-xl bg-background-100 text-foreground-800 font-semibold text-sm mb-3 cursor-pointer hover:bg-primary-50 transition-colors"
              >
                <i className="ri-book-open-line text-lg text-primary-500"></i>
                Shopping Guide
              </Link>

              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl bg-background-100 text-foreground-800 font-semibold text-sm mb-1 cursor-pointer hover:bg-primary-50 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-grid-line text-lg text-primary-500"></i>
                  Kategorien
                </span>
                <i className={`${categoryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
              </button>

              {categoryOpen && (
                <div className="ml-2 mb-3 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-primary-400`}></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}

              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl bg-background-100 text-foreground-800 font-semibold text-sm mb-1 cursor-pointer hover:bg-secondary-50 transition-colors"
              >
                <span className="flex items-center gap-3">
                  <i className="ri-article-line text-lg text-secondary-500"></i>
                  SEO Guides
                </span>
                <i className={`${guideOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
              </button>

              {guideOpen && (
                <div className="ml-2 mb-3 space-y-0.5">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/artikel/${article.slug}`}
                      onClick={() => setMobileOpen(false)}
                      className="block px-4 py-2.5 rounded-lg text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="font-medium text-foreground-700">{article.title}</div>
                      <div className="text-foreground-400 mt-0.5">{article.readingTime} Min. Lesezeit</div>
                    </Link>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="block text-center px-5 py-3.5 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold cursor-pointer hover:bg-foreground-800 transition-colors"
                >
                  Zu kaspreadsheet.com
                  <i className="ri-arrow-right-up-line ml-1.5 text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}